/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/RankChart.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useState } from 'react';
import './charts.css';

/**
 * RankChart — magnitude across categories.
 *
 * Horizontal bars, sorted descending, ONE colour (--mark), a direct value
 * label on every row. Horizontal because category names are words and words
 * read horizontally; a vertical bar chart with 17 country names underneath is
 * a chart you have to tilt your head at.
 *
 * ONE measure, ONE colour. Bars are never coloured by rank or index — the
 * order already carries the ranking, and a colour ramp on a single measure
 * invents a second dimension that is not in the data.
 *
 * PROPS
 *   rows       array of objects
 *   labelKey   field holding the category name          (default 'label')
 *   valueKey   field holding the magnitude              (default 'value')
 *              values may be strings ("288") — coerced with Number()
 *   max        value that fills the track. Defaults to the largest row.
 *              Pass an explicit max to make two RankCharts comparable.
 *   unit       suffix printed after each figure, e.g. '€'  (optional)
 *   limit      show this many rows, collapse the rest behind "+N more"
 *              (optional — omit to show everything)
 *   format     (value, row) => string                    (default en-GB)
 *   sort       false to keep the order the caller passed (default true)
 *   loading    render the skeleton instead of the chart
 *   empty      text shown when there are no rows
 *   caption    short factual line under the chart
 *   moreLabel  (n) => string for the expand control
 *   lessLabel  string for the collapse control
 *   ariaLabel  overrides the generated text summary
 *   className  extra class on the <figure>
 *
 * Long labels ellipsise on one line and never wrap the layout; the full name
 * is in the row's title attribute and in the hover/focus tooltip. Rows are
 * focusable so that tooltip is reachable from the keyboard.
 */

function toNum(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function defaultFormat(v) {
  return Number(v).toLocaleString('en-GB');
}

function pctText(part, whole) {
  if (!(whole > 0)) return '—';
  const p = (part / whole) * 100;
  const digits = p > 0 && p < 10 ? 1 : 0;
  return p.toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) + '%';
}

export default function RankChart({
  rows,
  labelKey = 'label',
  valueKey = 'value',
  max,
  unit,
  limit,
  format,
  sort = true,
  loading = false,
  empty = 'No data.',
  caption,
  moreLabel,
  lessLabel = 'Show fewer',
  ariaLabel,
  className,
}) {
  const [open, setOpen] = useState(false);

  const fmt = typeof format === 'function' ? format : defaultFormat;
  const withUnit = (v) => fmt(v) + (unit ? (unit === '%' ? '' : ' ') + unit : '');

  const all = (Array.isArray(rows) ? rows.filter(Boolean) : []).map((r) => ({
    label: r[labelKey] === null || r[labelKey] === undefined || r[labelKey] === ''
      ? '—'
      : String(r[labelKey]),
    value: toNum(r[valueKey]),
    row: r,
  }));

  if (sort) all.sort((a, b) => b.value - a.value);

  if (loading) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'} aria-busy="true">
        <div className="chart-skeleton-rows">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className="skeleton" style={{ display: 'block', height: '22px' }} />
          ))}
        </div>
      </figure>
    );
  }

  if (all.length === 0) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'}>
        <div className="empty">{empty}</div>
        {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
      </figure>
    );
  }

  const cap = Number(limit);
  const collapsible = Number.isFinite(cap) && cap > 0 && all.length > cap;
  const shown = collapsible && !open ? all.slice(0, cap) : all;
  const hidden = collapsible ? all.length - cap : 0;

  const top = Number.isFinite(Number(max)) && Number(max) > 0
    ? Number(max)
    : all.reduce((m, r) => (r.value > m ? r.value : m), 0);

  const total = all.reduce((s, r) => s + r.value, 0);

  const summary =
    ariaLabel ||
    'Ranking of ' + all.length + ' categories. ' +
      all
        .slice(0, 5)
        .map((r) => r.label + ' ' + withUnit(r.value))
        .join(', ') +
      (all.length > 5 ? ' and ' + (all.length - 5) + ' more' : '') + '.';

  const more = typeof moreLabel === 'function'
    ? moreLabel(hidden)
    : '+' + hidden + ' more';

  return (
    <figure className={className ? 'chart ' + className : 'chart'}>
      <ol className="rk-chart" aria-label={summary}>
        {shown.map((r, i) => {
          const pct = top > 0 ? Math.max(0, Math.min(100, (r.value / top) * 100)) : 0;
          return (
            <li className="rk-row" key={r.label + '-' + i} tabIndex={0}>
              <span className="rk-label" title={r.label}>{r.label}</span>

              <span className="rk-track">
                <span
                  className="rk-fill"
                  style={{
                    display: 'block',
                    width: pct + '%',
                    minWidth: r.value > 0 ? '3px' : 0,
                    animationDelay: Math.min(i, 12) * 45 + 'ms',
                  }}
                />
              </span>

              <span className="rk-value num">{withUnit(r.value)}</span>

              <span className="chart-rowtip" role="presentation">
                <span className="chart-tip-head">{r.label}</span>
                <span className="chart-tip-row">
                  <span className="chart-tip-key">Value</span>
                  <span className="chart-tip-val">{withUnit(r.value)}</span>
                </span>
                <span className="chart-tip-row">
                  <span className="chart-tip-key">Of the largest</span>
                  <span className="chart-tip-val">{pctText(r.value, top)}</span>
                </span>
                <span className="chart-tip-row">
                  <span className="chart-tip-key">Of the total</span>
                  <span className="chart-tip-val">{pctText(r.value, total)}</span>
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      {collapsible ? (
        <div className="rk-foot">
          <button
            type="button"
            className="btn-ui rk-more"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? lessLabel : more}
          </button>
        </div>
      ) : null}

      {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
    </figure>
  );
}
